import { SearchSelect, SearchSelectOptionProps } from "@alm/flow-ds/components";
import { Actions } from "@alm/flow-ds/dist/components/SearchSelect/SearchSelect";
import { FormLayout } from "hooks/FormDesigner/useElementList";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { validateInputNumber } from "../helpers";

const FORM_MAX_WIDTHS = {
	desktop: [1080, 1366, 1920],
	tablet: [768],
	mobile: [320, 412],
};

interface Props {
	disabled: boolean;
	maxWidth: FormLayout["maxWidth"];
	updateFormLayoutMaxWidth: (data: number | null) => void;
}

const MaxWidth = ({ disabled, maxWidth, updateFormLayoutMaxWidth }: Props) => {
	const [_maxWidth, setMaxWidth] = useState<number | null>(maxWidth || null);
	const [value, setValue] = useState<{
		value: string;
		label: string;
	} | null>(null);
	const [options, setOptions] = useState<
		{
			label: string;
			options: SearchSelectOptionProps[];
		}[]
	>([]);

	const { t } = useTranslation();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleOnChange = (value: any, action?: Actions) => {
		if (action?.action === "clear") {
			setMaxWidth(null);
			return;
		}
		const validatedValue: number | null = validateInputNumber(
			+value.value,
			null,
			null
		);
		setMaxWidth(validatedValue);
	};

	useEffect(() => {
		const desktopOptions: SearchSelectOptionProps[] = [];
		const tabletOptions: SearchSelectOptionProps[] = [];
		const mobileOptions: SearchSelectOptionProps[] = [];

		FORM_MAX_WIDTHS.desktop.forEach((width, index) => {
			desktopOptions.push({
				value: width.toString(),
				label:
					width.toString() +
					(index === 0
						? ` (${t("formDesigner.labels.default")})`
						: ""),
			});
		});

		FORM_MAX_WIDTHS.tablet.forEach(width => {
			tabletOptions.push({
				value: width.toString(),
				label: width.toString(),
			});
		});

		FORM_MAX_WIDTHS.mobile.forEach(width => {
			mobileOptions.push({
				value: width.toString(),
				label: width.toString(),
			});
		});

		setOptions([
			{
				label: t("formDesigner.labels.desktop"),
				options: desktopOptions,
			},
			{
				label: t("formDesigner.labels.tablet"),
				options: tabletOptions,
			},
			{
				label: t("formDesigner.labels.mobile"),
				options: mobileOptions,
			},
		]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (_maxWidth === null) setValue(null);
		else
			setValue({
				value: _maxWidth?.toString() || "",
				label: _maxWidth?.toString() || "",
			});
	}, [_maxWidth]);

	return (
		<SearchSelect
			onChange={handleOnChange}
			onBlur={() => updateFormLayoutMaxWidth(_maxWidth)}
			label={t("formDesigner.labels.maxWidth")}
			hint={t("formDesigner.text.maxWidth")}
			className=""
			value={value}
			options={options}
			debounceTimeout={400}
			clearable={true}
			onCreate={input => handleOnChange({ value: input })}
			disabled={disabled}
			placeholder={t(`formDesigner.placeholders.select`)}
		/>
	);
};

export default MaxWidth;
